import { LoginForm } from "@/app/[locale]/(Authentication)/login/page";
import { RegisterForm } from "@/app/[locale]/(Authentication)/register/page";
import { ChangeLanguageForm } from "@/app/[locale]/(user-page)/(Account)/account/language/components/content/Content";
import { ChangePasswordForm } from "@/app/[locale]/(user-page)/(Account)/account/password-security/components/ChangePasswordPopup";
import { OrganizationForm } from "@/app/[locale]/(user-page)/components/popup/PopupOrganization";
import _APIInstance from "@/config/configApi";
import _APIInstanceServer from "@/config/configApiServer";
import { API } from "@/constants/common";
import { paramsLang } from "@/models/Params";
import { ConfigData } from "@/models/config";
import {
  CheckIsValidCode,
  ForgotPasswordData,
  User,
  UserSocialLogin,
} from "@/models/user";

export class UserServices {
  //Get User
  static async getMe(): Promise<User> {
    return await _APIInstance
      .get(`${API.AUTH.ME}`)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e);
      });
  }

  static async getMeByIdServer(): Promise<User> {
    try {
      const res = await _APIInstanceServer.get(`${API.AUTH.ME_BY_ID}`);
      return res?.data.data;
    } catch (e: any) {
      throw e?.response?.data;
    }
  }

  static async getMeServer(): Promise<User> {
    try {
      const response = await _APIInstanceServer
      .get(`${API.AUTH.ME}`);
      return response?.data?.data;
    } catch (error:any) {
      throw error?.response?.data;
    }
  }

  static async getConfig(): Promise<ConfigData> {
    return await _APIInstance
      .get(`${API.AUTH.CONFIG}`)
      .then((res) => {
        return Promise.resolve(res?.data?.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  //Login
  static async userSocialLogin(params: UserSocialLogin): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.LOGIN_SOCIAL}`, params)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  static async login(data: LoginForm): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.LOGIN, data)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  //Register
  static async checkInvitationCode(invitationCode: string): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.CHECK_INVITATION, { invitationCode })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async checkEmail(email: string): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.CHECK_EMAIL, { email })
      .then((res) => Promise.resolve(res.data.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async register(data: RegisterForm): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.REGISTER}`, data)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  static async register2(data: RegisterForm): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.REGISTER2}`, data)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  //Verify Email
  static async sendVerifyEmail(email: string): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.SEND_VERIFY_EMAIL, { email })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async active(code: string) {
    return await _APIInstance
      .post(API.AUTH.ACTIVE, {
        code,
      })
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  //Forgot Password
  static async sendEmailForgotPassword(email: string): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.SEND_EMAIL_FORGOT_PASSWORD, { email })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async checkIsValidCode(data: CheckIsValidCode) {
    return await _APIInstance
      .post(API.AUTH.CHECK_ISVALID_CODE, data)
      .then((res) => {
        return Promise.resolve(res.data.data);
      })
      .catch((e) => {
        return Promise.reject(e?.response?.data);
      });
  }

  static async resetPassword(data: ForgotPasswordData): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.FORGOT_PASSWORD, data)
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  //User
  static async updateAvatar(avatar: File): Promise<any> {
    const formData = new FormData();
    formData.append("avatar", avatar);

    return await _APIInstance
      .put(API.AUTH.UPDATE_AVATAR, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changeName(data: {
    firstName: string;
    lastName: string;
  }): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_NAME, data)
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changePhone(phone: string): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_PHONE, { phone })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changeCompanyName(companyName: string): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_COMPANY_NAME, { companyName })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changePassword(data: ChangePasswordForm): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_PASSWORD, data)
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changeLanguage(data: ChangeLanguageForm): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_LANGUAGE, data)
      .then((res) => Promise.resolve(res.data.data))
      .catch((e) => Promise.reject(e.response.data));
  }

  static async changeLanguageMutation(data: any): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_LANGUAGE, { language: data })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async changeCurrency(currency: string): Promise<any> {
    return await _APIInstance
      .put(API.AUTH.CHANGE_CURRENCY, { currency })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async sendOtpAuth2FA() {
    return _APIInstance
      .post(`${API.AUTH.SEND_OTP_AUTH}`)
      .then((res) => Promise.resolve(res?.data?.data))
      .catch((res) => Promise.reject(res?.response?.data));
  }

  static async optional2FA(data: FormData) {
    return await _APIInstance
      .put(`${API.AUTH.OPTIONAL_2FA}`, data)
      .then((res) => Promise.resolve(res))
      .catch((res) => Promise.reject(res));
  }

  static async accept2FA(data: FormData) {
    try {
      const response = await _APIInstance.post(`${API.AUTH.ACCEPT_2FA}`, data);
      return response?.data?.data;
    } catch (error: any) {
      throw error?.response?.data;
    }
  }

  static async reSendOTP(data: FormData) {
    try {
      const response = await _APIInstance.post(`${API.AUTH.RESEND_2FA}`, data);
      return response?.data?.data;
    } catch (error: any) {
      throw error?.response?.data;
    }
  }

  static async getSubscription(params: paramsLang) {
    return await _APIInstance
      .get(`${API.AUTH.SUBSCRIPTION}`, {
        params: params,
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }
  static async getSubscriptionServer(params: paramsLang) {
    try {
      const response = await _APIInstanceServer
      .get(`${API.AUTH.SUBSCRIPTION}`, {
        params: params,
      });
      return response?.data;
    } catch (error:any) {
      throw error?.response?.data;
    }
  }
  static async cancelSubscription(subscriptionId: number) {
    return await _APIInstance
      .delete(
        `${API.AUTH.CANCEL_SUBSCRIPTION}`.replace(
          ":subscriptionId",
          subscriptionId.toString()
        )
      )
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async cancelSubscriptionOrganization(id: number | undefined) {
    return await _APIInstance
      .post(
        `${API.AUTH.CANCEL_SUBSCRIPTION_ORGANIZATION}`.replace(":id", `${id}`)
      )
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async TrialSubscription(data: { subscriptionId: number | string }) {
    try {
      const response = await _APIInstanceServer
      .post(`${API.AUTH.ACTIVE_TRIAL}`, data);
      return response?.data;
    } catch (error:any) {
      throw error?.response?.data;
    }
  }

  static async checkDomainEmail(domain: string): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.CHECK_DOMAIN_EMAIL}`, {
        domainEmail: domain,
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }
  static async checkHistoryLog(token: string): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.CHECK_HISTORY_LOG}`, {
        token: token,
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async checkEmailBlocked(email: string): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.CHECK_EMAIL_BLOCKED}`, {
        email: email,
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }
  static async checkInVitedCode(invitedCode: string): Promise<any> {
    return await _APIInstance
      .post(`${API.AUTH.CHECK_INVITED_CODE}`, {
        invitedCode: invitedCode,
      })
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async updateOrganization(data: OrganizationForm) {
    return await _APIInstance
      .put(API.AUTH.UPDATE_ORGANIZATION, data)
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async turnOffWarning() {
    return await _APIInstance
      .put(API.AUTH.TURN_OFF_WARNING)
      .then((res) => Promise.resolve(res.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }

  static async verifyOtp(data: any): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.VERIFY_OTP, data)
      .then((res) => Promise.resolve(res.data?.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }
  static async resendOtpVerify(data: any): Promise<any> {
    return await _APIInstance
      .post(API.AUTH.RESEND_OTP_VERIFY, data)
      .then((res) => Promise.resolve(res.data?.data))
      .catch((e) => Promise.reject(e?.response?.data));
  }
}

export default UserServices;
